/**
 * Inbox confirm + narrow-gate auto-confirm (shared manual + auto paths).
 * @see docs/APP_UI_SPEC.md — D4
 */

import { APPLICATION_STATUSES } from './applicationStatus.js';
import { resolveOfferDateForInboxRow } from './bookingEmail.js';
import {
  rejectOtherPendingApplicationsSameVenueMonth,
  restoreRejectedApplicationsSameVenueMonth,
} from './applicationVenueMonthExclusivity.js';
import { setInboxRowGroundTruthIfUnset } from './inboxGroundTruth.js';

export const AUTO_CONFIRM_ENABLED = true;

/**
 * Auto-confirm when numeric confidence is **at least** this value (0.9 = 90% inclusive).
 * @see docs/APP_UI_SPEC.md — D4
 */
export const AUTO_CONFIRM_THRESHOLD = 0.9;

/**
 * Confirm one application and apply same-venue-month exclusivity (same as manual inbox confirm).
 *
 * @param {import('@supabase/supabase-js').SupabaseClient} supabase
 * @param {object} cand - candidate from `displayCandidateFromApplication`
 * @returns {Promise<{ exclusivity_pending_rejected_count: number }>}
 */
export async function confirmApplicationFromInboxCandidate(supabase, cand) {
  if (cand.status !== APPLICATION_STATUSES.OFFERED) {
    throw new Error('Application is not in offered status.');
  }
  const { error } = await supabase
    .from('applications')
    .update({ status: APPLICATION_STATUSES.CONFIRMED })
    .eq('id', cand.id);
  if (error) throw error;

  let exclusivity_pending_rejected_count = 0;
  const ev = cand.event;
  if (ev && ev.venue_id != null && ev.event_date) {
    const rejectedRows = await rejectOtherPendingApplicationsSameVenueMonth(
      supabase,
      { email: cand.email },
      {
        id: ev.id,
        venue_id: ev.venue_id,
        event_date: ev.event_date,
      }
    );
    exclusivity_pending_rejected_count = Array.isArray(rejectedRows) ? rejectedRows.length : 0;
  }
  return { exclusivity_pending_rejected_count };
}

/**
 * @param {object} row - email_inbox_log
 * @param {object[]} candidates - from `buildInboxCandidatesForRow`
 * @param {{ skipEnabledCheck?: boolean }} [opts]
 * @returns {{ ok: true, cand: object } | { ok: false, reason: string }}
 */
export function evaluateAutoConfirmGate(row, candidates, opts = {}) {
  if (!opts.skipEnabledCheck && !AUTO_CONFIRM_ENABLED) {
    return { ok: false, reason: 'Auto-confirm disabled.' };
  }
  if (String(row.parsed_intent || '').toLowerCase() !== 'confirm') {
    return { ok: false, reason: 'Intent is not confirm.' };
  }
  if (row.classification_source !== 'llm') {
    return { ok: false, reason: 'Classification source is not llm.' };
  }
  const conf = Number(row.confidence);
  if (Number.isNaN(conf) || conf < AUTO_CONFIRM_THRESHOLD) {
    return {
      ok: false,
      reason: `Confidence must be at least ${AUTO_CONFIRM_THRESHOLD} (${Math.round(AUTO_CONFIRM_THRESHOLD * 100)}%).`,
    };
  }
  const gt = row.ground_truth_intent;
  if (gt != null && String(gt).toLowerCase() !== 'confirm') {
    return { ok: false, reason: 'Ground truth disagrees with confirm.' };
  }
  if (row.auto_confirmed_at) {
    return { ok: false, reason: 'Already auto-confirmed.' };
  }

  const offerDate = resolveOfferDateForInboxRow(row);
  if (!offerDate) {
    return { ok: false, reason: 'No parsed offer date.' };
  }

  const offeredDateMatches = candidates.filter(
    (c) =>
      c.status === APPLICATION_STATUSES.OFFERED &&
      c.matchesEmailOfferDate &&
      c.event?.event_date === offerDate
  );
  if (offeredDateMatches.length !== 1) {
    if (offeredDateMatches.length > 1) {
      return { ok: false, reason: 'Multiple offered applications match offer date.' };
    }
    const sameDateAnyStatus = candidates.filter(
      (c) => c.matchesEmailOfferDate && c.event?.event_date === offerDate
    );
    if (sameDateAnyStatus.length > 0) {
      const statuses = [...new Set(sameDateAnyStatus.map((c) => String(c.status)))].join(', ');
      return {
        ok: false,
        reason: `No offered application matches offer date (${sameDateAnyStatus.length} application(s) match the parsed date but are not offered: ${statuses}).`,
      };
    }
    return { ok: false, reason: 'No single offered application matches offer date.' };
  }

  return { ok: true, cand: offeredDateMatches[0] };
}

/**
 * @param {import('@supabase/supabase-js').SupabaseClient} supabase
 * @param {object} row
 * @param {object[]} candidates
 * @param {{ set_by: string }} ctx
 * @returns {Promise<{ applied: boolean, applicationId?: number, notifyPayload?: object, reason?: string }>}
 */
export async function attemptAutoConfirm(supabase, row, candidates, ctx) {
  const gate = evaluateAutoConfirmGate(row, candidates, {});
  if (!gate.ok) {
    return { applied: false, reason: gate.reason };
  }

  const { exclusivity_pending_rejected_count } = await confirmApplicationFromInboxCandidate(
    supabase,
    gate.cand
  );

  const now = new Date().toISOString();
  const { error: patchErr } = await supabase
    .from('email_inbox_log')
    .update({
      auto_confirmed_at: now,
    })
    .eq('id', row.id);
  if (patchErr) throw patchErr;

  await setInboxRowGroundTruthIfUnset(supabase, row.id, {
    intent: 'confirm',
    application_ids: [gate.cand.id],
    set_by: ctx.set_by,
  });

  const notifyPayload = {
    inbox_log_id: row.id,
    subject: row.subject ?? '',
    body_snippet: row.body_snippet ?? '',
    sender_email: row.sender_email ?? '',
    parsed_intent: row.parsed_intent ?? '',
    confidence: row.confidence != null && row.confidence !== '' ? Number(row.confidence) : null,
    application_id: gate.cand.id,
    comedian_name: gate.cand.comedian_name ?? '',
    application_email: gate.cand.email ?? '',
    event_date: gate.cand.event?.event_date ?? '',
    event_name: gate.cand.event?.event_name ?? '',
    venue_name: gate.cand.event?.venue_name ?? '',
    exclusivity_pending_rejected_count,
  };

  return { applied: true, applicationId: gate.cand.id, notifyPayload, confirmCand: gate.cand };
}

/**
 * Undo auto-confirm: application back to offered, restore exclusivity cascade, clear audit column.
 *
 * @param {import('@supabase/supabase-js').SupabaseClient} supabase
 * @param {object} row - inbox row
 * @param {object} cand - candidate (still holds event + email)
 * @param {number} applicationId
 */
export async function undoAutoConfirmInbox(supabase, row, cand, applicationId) {
  const { error: u1 } = await supabase
    .from('applications')
    .update({ status: APPLICATION_STATUSES.OFFERED })
    .eq('id', applicationId);
  if (u1) throw u1;

  const ev = cand.event;
  if (ev && ev.venue_id != null && ev.event_date) {
    await restoreRejectedApplicationsSameVenueMonth(
      supabase,
      { email: cand.email },
      {
        id: ev.id,
        venue_id: ev.venue_id,
        event_date: ev.event_date,
      }
    );
  }

  const { error: u2 } = await supabase
    .from('email_inbox_log')
    .update({ auto_confirmed_at: null })
    .eq('id', row.id);
  if (u2) throw u2;
}
